// =============================================================================
// サウンド。環境ループ(ミラー同梱mp3: calm/storm/underwater)のクロスフェード切替と、
// WebAudio合成の効果音(採取/クラフト/食べる/サメ警告/勝利...)。外部音源は増やさない。
// ブラウザのautoplay制限: 初回のユーザー操作(pointerdown/keydown)でアンロックする。
// =============================================================================

let ctx: AudioContext | null = null;
let unlocked = false;
let master: GainNode | null = null;
let muted = false;
try { muted = localStorage.getItem('side6-muted') === '1'; } catch { /* ignore */ }

// ミュート切替(設定パネルから)。SE(master gain)と環境ループの両方に効く。設定は保存。
export function setMuted(on: boolean): void {
  muted = on;
  try { localStorage.setItem('side6-muted', on ? '1' : '0'); } catch { /* ignore */ }
  if (master) master.gain.value = on ? 0 : 0.5;
  for (const k in amb) amb[k].muted = on;
}
export function isMuted(): boolean { return muted; }
(window as any).__setMuted = setMuted;
(window as any).__isMuted = isMuted;

const amb: Record<string, HTMLAudioElement> = {};
let current: string | null = null;
let fadeTimer: number | null = null;

function ensureAmb(name: string): HTMLAudioElement {
  if (!amb[name]) {
    const a = new Audio(`/audio/${name}.mp3`);
    a.loop = true;
    a.volume = 0;
    a.muted = muted;
    a.preload = 'auto';
    amb[name] = a;
  }
  return amb[name];
}

// 初回ユーザー操作で音を有効化(autoplay制限解除)。start()から一度呼ぶ。
export function initAudio(): void {
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        ctx = new AC();
        master = ctx!.createGain();
        master.gain.value = muted ? 0 : 0.5;
        // 同時発音(嵐: 雷+足音+心音+スプラッシュ等)の合算クリップを防ぐリミッター
        const comp = ctx!.createDynamicsCompressor();
        comp.threshold.value = -12; comp.knee.value = 24; comp.ratio.value = 6;
        master.connect(comp); comp.connect(ctx!.destination);
      }
      if (ctx && ctx.state === 'suspended') ctx.resume();
    } catch { /* 音なしでも続行 */ }
    if (current) { const a = ensureAmb(current); a.play().catch(() => {}); }
    removeEventListener('pointerdown', unlock, true);
    removeEventListener('keydown', unlock, true);
    removeEventListener('touchstart', unlock, true);
  };
  addEventListener('pointerdown', unlock, { capture: true });
  addEventListener('keydown', unlock, { capture: true });
  addEventListener('touchstart', unlock, { capture: true });
}

// 環境ループの切替(1.2秒クロスフェード)。null で無音へ。
export function setAmbience(name: 'calm' | 'storm' | 'underwater' | null, vol = 0.32): void {
  if (name === current) return;
  const prev = current ? ensureAmb(current) : null;
  current = name;
  const next = name ? ensureAmb(name) : null;
  if (next && unlocked) next.play().catch(() => {});
  if (fadeTimer != null) clearInterval(fadeTimer);
  const t0 = performance.now(), DUR = 1200, pv = prev ? prev.volume : 0;
  fadeTimer = window.setInterval(() => {
    const t = Math.min(1, (performance.now() - t0) / DUR);
    if (prev) prev.volume = pv * (1 - t);
    if (next) next.volume = vol * t;
    if (t >= 1) { if (prev) prev.pause(); if (fadeTimer != null) { clearInterval(fadeTimer); fadeTimer = null; } }
  }, 60);
}

// ---- 合成SE ------------------------------------------------------------------
function osc(type: OscillatorType, f0: number, f1: number, dur: number, vol: number, delay = 0): void {
  if (!ctx || !master) return;
  const t = ctx.currentTime + delay;
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t);
  if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(master);
  o.start(t); o.stop(t + dur + 0.02);
}
function thunk(vol: number, delay = 0, freq = 600, dur = 0.09): void { // 打撃(ノイズバースト+lowpass)
  if (!ctx || !master) return;
  const t = ctx.currentTime + delay;
  const n = ctx.sampleRate * dur, buf = ctx.createBuffer(1, n, ctx.sampleRate), d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = ctx.createBufferSource(); src.buffer = buf;
  const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = freq;
  const g = ctx.createGain(); g.gain.value = vol;
  src.connect(f); f.connect(g); g.connect(master);
  src.start(t);
}

export type Sfx = 'gather' | 'pickup' | 'craft' | 'eat' | 'drink' | 'board' | 'place' | 'shark' | 'boss' | 'hurt' | 'rest' | 'win' | 'death' | 'repair' | 'fish' | 'heartbeat' | 'thunder' | 'step' | 'swim' | 'splash' | 'thud' | 'gull' | 'cast' | 'feat' | 'rare' | 'discover';

export function sfx(kind: Sfx): void {
  if (!ctx || !master) return;
  switch (kind) {
    case 'gather': thunk(0.5); break;                                            // コッ(採取ヒット)
    case 'pickup': osc('sine', 780, 1240, 0.09, 0.18); break;                    // ピッ(入手)
    case 'craft': thunk(0.4); thunk(0.4, 0.12); osc('sine', 620, 930, 0.1, 0.14, 0.24); break; // トントン+完成音
    case 'eat': osc('sawtooth', 150, 110, 0.07, 0.16); osc('sawtooth', 140, 100, 0.07, 0.16, 0.12); break;
    case 'drink': osc('sine', 520, 260, 0.16, 0.16); osc('sine', 600, 300, 0.14, 0.13, 0.14); break;
    case 'board': thunk(0.55); osc('sine', 95, 60, 0.16, 0.3, 0.02); break;      // ドスン
    case 'place': thunk(0.5); osc('sine', 240, 180, 0.12, 0.2, 0.05); break;
    case 'shark': osc('sawtooth', 58, 44, 0.55, 0.34); osc('sawtooth', 62, 46, 0.55, 0.3, 0.3); break; // 不穏な低音
    case 'boss': // 主の出現スティンガー: 超低音の衝撃+三全音(55↔78Hz)の不協和な唸り=通常ザメと音で区別
      thunk(0.7);
      osc('sine', 36, 30, 1.2, 0.5, 0.05);
      osc('sawtooth', 55, 52, 1.4, 0.22, 0.15);
      osc('sawtooth', 78, 74, 1.4, 0.18, 0.35);
      break;
    case 'hurt': osc('square', 180, 90, 0.16, 0.3); break;                       // 被ダメ
    case 'rest': osc('sine', 392, 392, 0.22, 0.14); osc('sine', 494, 494, 0.24, 0.13, 0.2); break;
    case 'repair': thunk(0.45); thunk(0.45, 0.14); break;
    case 'heartbeat': osc('sine', 55, 42, 0.14, 0.5); osc('sine', 50, 40, 0.11, 0.38, 0.22); break; // ドッ・ドッ(低体力の心音)
    case 'fish': osc('sine', 660, 990, 0.1, 0.18); osc('sine', 880, 1320, 0.12, 0.16, 0.1); break;
    case 'thunder': // 遠雷: 低域ノイズを3連で重ねた尾+サブの唸り
      thunk(0.6); thunk(0.5, 0.18); thunk(0.35, 0.42);
      osc('sine', 44, 26, 1.6, 0.4, 0.05);
      break;
    case 'step': thunk(0.13, 0, 280 + Math.random() * 140, 0.055); break;        // 砂を踏む(低音量+ピッチ揺らぎで機械感を消す)
    case 'swim': thunk(0.09, 0, 850 + Math.random() * 300, 0.24); break;         // 水かき(高めのノイズを長めに=スウィッシュ)
    case 'splash': // 入水: 高域の飛沫+泡の尾+沈み込みの低音
      thunk(0.34, 0, 1500, 0.16); thunk(0.2, 0.07, 900, 0.3);
      osc('sine', 230, 90, 0.2, 0.12, 0.02);
      break;
    case 'thud': thunk(0.3, 0, 420, 0.08); osc('sine', 115, 70, 0.11, 0.16); break; // 高所からの着地
    case 'cast': // 釣りキャスト: 竿のスウィッシュ+ウキの小さな着水
      thunk(0.12, 0, 1300, 0.2);
      thunk(0.15, 0.2, 1000, 0.1);
      osc('sine', 280, 110, 0.07, 0.07, 0.2);
      break;
    case 'gull': { // カモメ(昼の浜の気配)。三角波の下降スイープ2〜3声、低音量で遠くに
      const p0 = 1700 + Math.random() * 500;
      osc('triangle', p0, p0 * 0.62, 0.34, 0.055);
      osc('triangle', p0 * 0.92, p0 * 0.55, 0.28, 0.045, 0.4);
      if (Math.random() < 0.5) osc('triangle', p0 * 1.05, p0 * 0.6, 0.22, 0.035, 0.85);
      break;
    }
    case 'feat': // 実績達成ファンファーレ: 軽快なアルペジオ+締めのオクターブ和音(winよりテンポ速く短い)
      [392, 523, 659, 784].forEach((f, i) => osc('triangle', f, f, 0.22, 0.16, i * 0.09));
      osc('triangle', 1047, 1047, 0.5, 0.14, 0.36);
      osc('sine', 523, 523, 0.5, 0.10, 0.36);
      break;
    case 'rare': // レア素材: 高域ベルのキラキラ3連+シマー(星が瞬く音)
      osc('sine', 1319, 1319, 0.14, 0.14);
      osc('sine', 1760, 1760, 0.14, 0.13, 0.09);
      osc('sine', 2637, 2637, 0.3, 0.11, 0.18);
      osc('sine', 2093, 2093, 0.4, 0.05, 0.26);
      break;
    case 'discover': // 島発見: おおらかな上昇2音(新天地の広がり)
      osc('triangle', 330, 440, 0.4, 0.16);
      osc('triangle', 440, 587, 0.55, 0.15, 0.3);
      osc('sine', 880, 880, 0.5, 0.05, 0.55);
      break;
    case 'win': [523, 659, 784, 1047].forEach((f, i) => osc('triangle', f, f, 0.35, 0.2, i * 0.16)); break;
    case 'death': [440, 349, 294].forEach((f, i) => osc('triangle', f, f * 0.98, 0.5, 0.2, i * 0.3)); break;
  }
}
